export const FLIPKART_ADS_OAUTH_MESSAGE_SOURCE = 'flipkart-ads-oauth';
export const FLIPKART_ADS_LOGIN_WRAPPER_ID = 'fkAdsLoginWrapper';

export type FlipkartAdsOauthMessage = {
  source: typeof FLIPKART_ADS_OAUTH_MESSAGE_SOURCE;
  code: string | null;
  error: string | null;
};

export function flipkartAdsConnectMessage(reused: boolean): string {
  return reused ? 'Flipkart Ads connection updated' : 'Flipkart Ads connected';
}

export function extractFlipkartAdsOauthCode(data: unknown): string | null {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed || null;
  }
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  for (const key of ['code', 'oauth_code', 'oauthCode']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

export function parseFlipkartAdsOauthMessage(data: unknown): FlipkartAdsOauthMessage | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if (record.source !== FLIPKART_ADS_OAUTH_MESSAGE_SOURCE) return null;
  return {
    source: FLIPKART_ADS_OAUTH_MESSAGE_SOURCE,
    code: typeof record.code === 'string' && record.code.trim() ? record.code.trim() : null,
    error: typeof record.error === 'string' && record.error.trim() ? record.error.trim() : null,
  };
}

export function allowedFlipkartAdsMessageOrigins(
  pageOrigin: string,
  apiBaseUrl?: string | null
): string[] {
  const origins = new Set<string>();
  if (pageOrigin) origins.add(pageOrigin);
  const configured = apiBaseUrl?.trim();
  if (configured) {
    try {
      origins.add(new URL(configured).origin);
    } catch {
      // Ignore invalid API base URLs; same-origin still applies.
    }
  }
  return [...origins];
}

export function parseFlipkartAdsOauthMessageEvent(
  event: Pick<MessageEvent, 'origin' | 'data'>,
  allowedOrigins: string[]
): FlipkartAdsOauthMessage | null {
  if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
    return null;
  }
  return parseFlipkartAdsOauthMessage(event.data);
}

export function formatFlipkartAdsSdkError(error: unknown): string {
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    for (const key of ['message', 'error_description', 'error', 'error_message']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Flipkart Ads authorization failed';
}

export function loadFlipkartAdsSdk(src: string): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Flipkart Ads SDK requires a browser'));
  }
  if (typeof window !== 'undefined' && window.FKOAuthClient) {
    return Promise.resolve();
  }
  const existing = document.querySelector<HTMLScriptElement>('script[data-fk-ads-sdk="1"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.FKOAuthClient) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Flipkart Ads SDK')),
        { once: true }
      );
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.fkAdsSdk = '1';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Flipkart Ads SDK'));
    document.body.appendChild(script);
  });
}

export type FKOAuthClientInstance = { init: () => unknown };

export type FKOAuthClientOptions = {
  clientId: string;
  redirectUri: string;
  element: string;
  onSuccess: (data: unknown) => void;
  onError: (error: unknown) => void;
};

declare global {
  interface Window {
    FKOAuthClient?: new (options: FKOAuthClientOptions) => FKOAuthClientInstance;
  }
}
